import { useState, useEffect, useCallback } from 'preact/hooks';
import emitter from '@webcore/core/events/eventemitter';
import appEvents from '@core/events/events';
import getUserDevice from '@core/utils/getUserDevice';
import { PlacementOverlay } from './PlacementOverlay';
import { CoachingOverlay } from '@core/components/CoachingOverlay/CoachingOverlay';
import localizationEvents from '../events/localizationEvents';
import coachingImage from '@assets/images/coaching-image.webp'
type LocalizationMode = 'localizing' | 'placing';

interface AROverlayProps {
  onExit: () => void;
  isRecording?: boolean;
  /** When true, shows CoachingOverlay during localizing and tap-to-place only during placing fallback */
  useLocalization?: boolean;
}

export function AROverlay({ onExit, isRecording = false, useLocalization = false }: AROverlayProps) {
  const [previewVisible, setPreviewVisible] = useState(true);
  const [objectPlaced, setObjectPlaced] = useState(false);

  // Localization mode (queens-gardens): localization state
  const [localizationMode, setLocalizationMode] = useState<LocalizationMode>('localizing');
  const [localizationUiHidden, setLocalizationUiHidden] = useState(false);

  // Shared: content events
  useEffect(() => {
    const handlePreviewVisible = () => {
      setPreviewVisible(true);
    };

    const handleContentReady = () => {
      setObjectPlaced(true);
    };

    const handleContentPositioned = () => {
      // User tapped to place OR localization succeeded (Immersal)
      setObjectPlaced(true);
    };

    const handleRepositioning = () => {
      setObjectPlaced(false);
      setPreviewVisible(true);
    };

    emitter.on('content-loading', handlePreviewVisible);
    emitter.on('content-ready', handleContentReady);
    appEvents.on('content-positioned', handleContentPositioned);
    (emitter as any).on('content-repositioning', handleRepositioning);

    return () => {
      emitter.off('content-loading', handlePreviewVisible);
      emitter.off('content-ready', handleContentReady);
      appEvents.off('content-positioned', handleContentPositioned);
      (emitter as any).off('content-repositioning', handleRepositioning);
    };
  }, []);

  // Localization mode: subscribe to localization events
  useEffect(() => {
    if (!useLocalization) return;

    const handleModeChange = (mode: LocalizationMode) => {
      setLocalizationMode(mode);
      setObjectPlaced(false);
      if (mode === 'localizing') {
        setLocalizationUiHidden(false);
      }
    };

    const handleLocalizationCount = (payload: { count: number; threshold: number }) => {
      setLocalizationUiHidden(payload.count >= payload.threshold);
    };

    const handlePlacementState = (payload: { placed: boolean }) => {
      setObjectPlaced(payload.placed);
    };

    localizationEvents.on('localization-mode-changed', handleModeChange);
    localizationEvents.on('localization-success-count', handleLocalizationCount);
    (emitter as any).on('placement-state', handlePlacementState);

    localizationEvents.emit('request-localization-state');
    (emitter as any).emit('request-placement-state');

    return () => {
      localizationEvents.off('localization-mode-changed', handleModeChange);
      localizationEvents.off('localization-success-count', handleLocalizationCount);
      (emitter as any).off('placement-state', handlePlacementState);
    };
  }, [useLocalization]);

  const handleTapToPlace = useCallback(
    (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (useLocalization) {
        if (localizationMode === 'placing' && !objectPlaced) {
          setObjectPlaced(true);
          appEvents.emit('content-positioned');
        }
      } else if (previewVisible && !objectPlaced) {
        setObjectPlaced(true);
        appEvents.emit('content-positioned');
      }
    },
    [useLocalization, localizationMode, objectPlaced, previewVisible]
  );

  const handleReposition = useCallback(() => {
    (emitter as any).emit('reposition');
  }, []);

  const handleRestartLocalization = useCallback(() => {
    if (localizationMode === 'localizing') {
      localizationEvents.emit('restart-localization');
    }
  }, [localizationMode]);

  const handleCoachingClose = useCallback(() => {
    setLocalizationMode('placing');
    setLocalizationUiHidden(true);
    localizationEvents.emit('switch-to-placing');
  }, []);

  // Simple mode: show tap-to-place when preview visible and not placed
  const showTapToPlaceSimple = !useLocalization && previewVisible && !objectPlaced;

  // Localization mode: show CoachingOverlay during localizing, tap-to-place during placing
  const isLocalizing = useLocalization && localizationMode === 'localizing';
  const isPlacing = useLocalization && localizationMode === 'placing';
  const isLocalizationUiVisible = isLocalizing && !isRecording && !localizationUiHidden;
  const showTapToPlaceLocalization = isPlacing && !objectPlaced;

  const showTapToPlace = showTapToPlaceSimple || showTapToPlaceLocalization;
  const { device } = getUserDevice();
  const isIOS = device === 'iOS' || device === 'AppClip';
  const topInsetOffset = isIOS ? '0px' : '50px';

  return (
    <div
      className="fixed inset-0"
      style={{
        pointerEvents: 'none',
        width: '100vw',
        height: '100dvh',
        minHeight: '100dvh',
      }}
    >
      {/* Tap to place overlay - full screen tap target */}
      {showTapToPlace && (
        <div
          className="absolute inset-0"
          onClick={handleTapToPlace}
          style={{ touchAction: 'none', pointerEvents: 'auto' }}
        />
      )}

      {/* Placement visual overlay (replaces "tap to place" text during placing) */}
      {showTapToPlace && <PlacementOverlay />}

      {/* CoachingOverlay - only when useLocalization and in localizing mode */}
      {useLocalization && isLocalizationUiVisible && (
        <CoachingOverlay
          currentContentIcon={coachingImage}
          type="immersal"
          variant="smallImageKeepText"
          onClose={handleCoachingClose}
        />
      )}

      {/* Close button - Top Left (hidden when recording, above coaching overlay) */}
      {!isRecording && (
        <div
          className="absolute z-[60]"
          style={{
            pointerEvents: 'auto',
            top: `calc(env(safe-area-inset-top, 0px) + ${topInsetOffset})`,
            left: '20px',
          }}
        >
          <button
            onClick={onExit}
            className="flex justify-center items-center hover:opacity-80"
            aria-label="Close AR experience"
          >
            <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
              <circle cx="25" cy="25" r="25" fill="black" fillOpacity="0.5" />
              <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
              <path
                d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Reposition button - Top Right (only in manual placing mode after timeout; hidden when localization succeeded) */}
      {!isRecording && objectPlaced && (!useLocalization || isPlacing) && (
        <div
          className="absolute z-[60]"
          style={{
            pointerEvents: 'auto',
            top: `calc(env(safe-area-inset-top, 0px) + ${topInsetOffset})`,
            right: '20px',
          }}
        >
          <button
            onClick={useLocalization && isLocalizing ? handleRestartLocalization : handleReposition}
            className="flex justify-center items-center hover:opacity-80"
            aria-label={
              useLocalization && isLocalizing ? 'Restart localization' : 'Reposition object'
            }
          >
            <svg width="54" height="54" viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_2835_2761" style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="0" width="54" height="54">
                <circle cx="27" cy="27" r="27" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_2835_2761)">
                <rect x="-40" width="134" height="53.9089" fill="black" fillOpacity="0.5"/>
              </g>
              <g filter="url(#filter0_i_2835_2761)">
                <circle cx="27" cy="27" r="27" fill="url(#paint0_linear_2835_2761)" fillOpacity="0.1"/>
              </g>
              <circle cx="27" cy="27" r="26" stroke="white" strokeWidth="2"/>
              <g filter="url(#filter1_d_2835_2761)">
                <path d="M31.6668 16.6667L27.0002 12L22.3335 16.6667" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <path d="M22.3332 37.3333L26.9998 42L31.6665 37.3333" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <path d="M37.3333 31.6667L42 27L37.3333 22.3334" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <path d="M16.6667 22.3333L12 27L16.6667 31.6666" stroke="white" strokeWidth="2" strokeLinecap="square"/>
                <circle cx="27" cy="27" r="2" fill="white"/>
                <path d="M28 40.416C28 40.9683 27.5523 41.416 27 41.416C26.4477 41.416 26 40.9683 26 40.416V32.9061C26 32.3859 26.4797 32 27 32C27.5203 32 28 32.3859 28 32.9061V40.416ZM40.416 26C40.9683 26 41.416 26.4477 41.416 27C41.416 27.5523 40.9683 28 40.416 28H32.9061C32.3859 28 32 27.5203 32 27C32 26.4797 32.3859 26 32.9061 26H40.416ZM21.0939 26C21.6141 26 22 26.4797 22 27C22 27.5203 21.6141 28 21.0939 28H13.583C13.0307 28 12.583 27.5523 12.583 27C12.583 26.4477 13.0307 26 13.583 26H21.0939ZM28 21.093C28 21.6132 27.5202 22 27 22C26.4798 22 26 21.6132 26 21.093V13.583C26 13.0307 26.4477 12.583 27 12.583C27.5523 12.583 28 13.0307 28 13.583V21.093Z" fill="white"/>
              </g>
              <defs>
                <filter id="filter0_i_2835_2761" x="0" y="0" width="54" height="54" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset/>
                  <feGaussianBlur stdDeviation="5"/>
                  <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0"/>
                  <feBlend mode="normal" in2="shape" result="effect1_innerShadow_2835_2761"/>
                </filter>
                <filter id="filter1_d_2835_2761" x="6.58594" y="10.5858" width="40.8281" height="40.8284" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                  <feOffset dy="4"/>
                  <feGaussianBlur stdDeviation="2"/>
                  <feComposite in2="hardAlpha" operator="out"/>
                  <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2835_2761"/>
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2835_2761" result="shape"/>
                </filter>
                <linearGradient id="paint0_linear_2835_2761" x1="27" y1="0" x2="27" y2="54" gradientUnits="userSpaceOnUse">
                  <stop stopOpacity="0"/>
                  <stop offset="1"/>
                </linearGradient>
              </defs>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
